import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { AGENT_API_URL, agentAxiosObj } from "../../../config/apiClients";
import { AGENT_STORAGE_KEYS } from "../../../config/storageKeys";
import { AgentAuthState } from "../../types";

const forgotPasswordInitialState = {
  isLoading: false,
  email: '',
  status: ''
}

interface LoginFormData {
  email: string;
  password: string;
}

const getStoredAgentUser = () => {
  const stored = localStorage.getItem(AGENT_STORAGE_KEYS.userInfo);
  return stored ? JSON.parse(stored) : undefined;
};

const initialState: AgentAuthState = {
  isAuthenticated: Boolean(getStoredAgentUser()),
  isLoading: false,
  user: getStoredAgentUser(),
  forgotPassword: forgotPasswordInitialState
};

export const loginAgent = createAsyncThunk(
  "/agentAuth/login",
  async (formData: LoginFormData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${AGENT_API_URL}/auth/login`, formData, { withCredentials: true });
      return response.data;
    } catch (error: any) {
      const responseData = error.response?.data;
      const errorMessage =
        responseData?.payload?.message ||
        responseData?.message ||
        error.message ||
        "Unable to log in due to a server issue. Please try again later.";
      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status,
        fullError: responseData,
      });
    }
  }
);

export const sendAgentResetEmail = createAsyncThunk(
  "/auth/resendOneTimePassword",
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await agentAxiosObj.post('/auth/resendOneTimePassword', { email });
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const verifyAgentOTP = createAsyncThunk(
  "/auth/verifyOneTimePassword",
  async ({ email, passcode }: { email: string; passcode: string }, { rejectWithValue }) => {
    try {
      const response = await agentAxiosObj.patch('/auth/verifyOneTimePassword', { email, passcode });
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateAgentPassword = createAsyncThunk(
  "/auth/addNewPassword",
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await agentAxiosObj.post('/auth/addNewPassword', { email, password });
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const agentAuthSlice = createSlice({
  name: "agentAuth",
  initialState,
  reducers: {
    logOutAgent: (state) => {
      state.isAuthenticated = false;
      state.user = undefined;
      localStorage.removeItem(AGENT_STORAGE_KEYS.userInfo);
    },
    checkAgentAuthStatus: (state) => {
      state.user = getStoredAgentUser();
      state.isAuthenticated = Boolean(state.user);
    },
    setEmail: (state, action) => {
      state.forgotPassword.email = action.payload
    }
  },
  extraReducers(builder) {
    builder
      .addCase(loginAgent.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loginAgent.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = action.payload.status === "success";
        if (action.payload.payload) {
          state.user = {
            _id: action.payload.payload._id,
            name: action.payload.payload.name,
            email: action.payload.payload.email,
            crmUserId: action.payload.payload.crmUserId
          };
        }
        if (state.isAuthenticated) {
          localStorage.setItem(AGENT_STORAGE_KEYS.userInfo, JSON.stringify(state.user));
        }
      })
      .addCase(loginAgent.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = undefined;
      })
      .addCase(sendAgentResetEmail.pending, (state) => {
        state.forgotPassword.isLoading = true;
      })
      .addCase(sendAgentResetEmail.fulfilled, (state, action) => {
        state.forgotPassword.isLoading = false;
        state.forgotPassword.status = action.payload.status;
      })
      .addCase(sendAgentResetEmail.rejected, (state) => {
        state.forgotPassword.isLoading = false;
        state.forgotPassword.status = '';
      })
      .addCase(verifyAgentOTP.pending, (state) => {
        state.forgotPassword.isLoading = true;
      })
      .addCase(verifyAgentOTP.fulfilled, (state, action) => {
        state.forgotPassword.isLoading = false;
        state.forgotPassword.status = action.payload.status;
      })
      .addCase(verifyAgentOTP.rejected, (state) => {
        state.forgotPassword.isLoading = false;
        state.forgotPassword.status = '';
      })
      .addCase(updateAgentPassword.pending, (state) => {
        state.forgotPassword.isLoading = true;
      })
      .addCase(updateAgentPassword.fulfilled, (state, action) => {
        state.forgotPassword.isLoading = false;
        state.forgotPassword.status = action.payload.status;
      })
      .addCase(updateAgentPassword.rejected, (state) => {
        state.forgotPassword.isLoading = false;
        state.forgotPassword.status = '';
      });
  },
});

export const { logOutAgent, checkAgentAuthStatus, setEmail } = agentAuthSlice.actions;

export const logoutAgent = createAsyncThunk("/agentAuth/logout", async (_, { dispatch }) => {
  dispatch(logOutAgent());
  await agentAxiosObj.post("/auth/logout").catch(() => undefined);
});
export default agentAuthSlice.reducer;